import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import { getFilteredGraphConstituents } from '../components/AstronautDataExplorer/util/CustomZoomDomainFiltering';
import { DataChartState, DataChartPayload } from './types/dataChartTypes';

export const initialState: DataChartState = {
  chartsData: {},
  isDataChartsError: false,
  errorMessage: '',
};

export const dataChartSlice = createSlice({
  name: 'dataChart',
  initialState,
  reducers: {
    setDataChartsData: (state, action: PayloadAction<DataChartPayload>) => {
      const { chartId, chartData, startDate, endDate } = action.payload;
      const newState = cloneDeep(state);

      newState.chartsData[chartId] = chartData;
      newState.isDataChartsError = false;
      newState.errorMessage = '';
      newState.currentDomain = [new Date(startDate), new Date(endDate)];

      chartData.forEach((currentGraphInfo) => {
        if (currentGraphInfo.type === 'status') {
          const statusChartColorDictionary: Record<string, string> = {};
          currentGraphInfo.graphs[0].colors?.forEach((currentColor) => {
            statusChartColorDictionary[currentColor.value] = currentColor.hex;
          });
          currentGraphInfo.graphs[0].colors = statusChartColorDictionary as any;

          const statusChartActivities = currentGraphInfo.graphs[0].axesData;
          currentGraphInfo.graphs[0].axesData = statusChartActivities.map(
            (currentStatusActivity, currentStatusActivityIndex) => {
              const nextActivityIndex =
                currentStatusActivityIndex === statusChartActivities.length - 1
                  ? undefined
                  : currentStatusActivityIndex + 1;
              return {
                x: new Date(currentStatusActivity.x).toISOString(),
                time: new Date(currentStatusActivity.x).toISOString(),
                name: currentStatusActivity.y as string,
                ...(currentStatusActivity.description?.robotId !==
                  undefined && {
                  robotId: currentStatusActivity.description.robotId,
                }),
                endTime: nextActivityIndex
                  ? new Date(
                      statusChartActivities[nextActivityIndex].x,
                    ).toISOString()
                  : undefined,
              };
            },
          ) as any;
        } else {
          // else it is a line chart
          const graphConstituents = getFilteredGraphConstituents(
            currentGraphInfo.graphs[0].axesData as any,
            [new Date(startDate), new Date(endDate)],
            true,
          );
          currentGraphInfo.graphDates = graphConstituents.graphDates;
          currentGraphInfo.graphValues = graphConstituents.graphValues;
        }
      });

      return newState;
    },

    setDataChartsError: (state, action: PayloadAction<string>) => {
      state.chartsData = {};
      state.isDataChartsError = true;
      state.errorMessage = action.payload;
    },

    resetDomain: (state) => {
      state.currentDomain = undefined;
    },

    resetChartsData: (state) => {
      state.chartsData = {};
    },
  },
});

export const {
  setDataChartsData,
  setDataChartsError,
  resetDomain,
  resetChartsData,
} = dataChartSlice.actions;

// Action creator for saga to listen to (not a slice action)
export const makeDataChartUpdateRequest = createAction(
  'UPDATE_DATA_CHART_REQUESTED',
  (
    entityId: string,
    startDate: string,
    endDate: string,
    linkedEntityId?: string,
    navigate?: any,
  ) => ({
    payload: {
      entityId,
      startDate,
      endDate,
      linkedEntityId,
      navigate,
    },
  }),
);

export default dataChartSlice.reducer;
